/**
 * Single Video Section - Custom Element
 * Handles play/pause, mute/unmute, progress bar, and thumbnail overlay.
 */

class singleVideoSection extends HTMLElement {
  constructor() {
    super();

    this.video = this.querySelector('.single-video-section__video');
    if (!this.video) return;

    this.wrapper = this.querySelector('.single-video-section__wrapper');
    this.thumbnailOverlay = this.querySelector('.single-video-section__thumbnail-overlay');
    this.playPauseBtn = this.querySelector('.single-video-section__btn--play-pause');
    this.muteBtn = this.querySelector('.single-video-section__btn--mute');
    this.progressWrap = this.querySelector('.single-video-section__progress-wrap');
    this.progressBar = this.querySelector('.single-video-section__progress-bar');
    this.timeDisplay = this.querySelector('.single-video-section__time');
    this.controls = this.querySelector('.single-video-section__controls');
    this.hideTimeout = null;

    this.initLanguageSwitch();
    this.bindEvents();
    this.updateMuteState();
  }

  /**
   * Detect language from URL and switch video source + thumbnail
   */
  initLanguageSwitch() {
    const isGerman = window.location.pathname.indexOf('/de') !== -1;
    const enSrc = this.video.dataset.srcEn || '';
    const deSrc = this.video.dataset.srcDe || '';

    if (isGerman && deSrc) {
      this.video.src = deSrc;
    } else if (enSrc) {
      this.video.src = enSrc;
    }

    // Switch thumbnail image
    if (this.thumbnailOverlay) {
      const thumbImg = this.thumbnailOverlay.querySelector('.single-video-section__thumbnail-img');
      if (thumbImg) {
        const enThumb = thumbImg.dataset.srcEn || '';
        const deThumb = thumbImg.dataset.srcDe || '';

        if (isGerman && deThumb) {
          thumbImg.src = deThumb;
          thumbImg.srcset = deThumb;
        } else if (enThumb) {
          thumbImg.src = enThumb;
          thumbImg.srcset = enThumb;
        }
      }
    }

    this.video.load();
  }

  /**
   * Bind all event listeners
   */
  bindEvents() {
    // Thumbnail overlay click
    if (this.thumbnailOverlay) {
      this.thumbnailOverlay.addEventListener('click', () => {
        this.thumbnailOverlay.classList.add('is-hidden');
        this.video.play();
      });
    }

    // Control buttons
    if (this.playPauseBtn) this.playPauseBtn.addEventListener('click', () => this.togglePlay());
    if (this.muteBtn) this.muteBtn.addEventListener('click', () => this.toggleMute());
    if (this.progressWrap) this.progressWrap.addEventListener('click', (e) => this.seekTo(e));

    // Video events
    this.video.addEventListener('timeupdate', () => this.updateProgress());
    this.video.addEventListener('play', () => {
      this.updatePlayState();
      if (this.thumbnailOverlay) this.thumbnailOverlay.classList.add('is-hidden');
    });
    this.video.addEventListener('pause', () => this.updatePlayState());
    this.video.addEventListener('ended', () => {
      this.updatePlayState();
      if (this.thumbnailOverlay) this.thumbnailOverlay.classList.remove('is-hidden');
      if (this.progressBar) this.progressBar.style.width = '0%';
    });
    this.video.addEventListener('loadedmetadata', () => {
      if (this.timeDisplay) this.timeDisplay.textContent = '0:00 / ' + this.formatTime(this.video.duration);
      this.updateMuteState();
    });
    this.video.addEventListener('volumechange', () => this.updateMuteState());

    // Show controls on touch (mobile)
    if (this.wrapper) {
      this.wrapper.addEventListener('touchstart', () => {
        if (this.controls) {
          this.controls.classList.add('is-visible');
          clearTimeout(this.hideTimeout);
          this.hideTimeout = setTimeout(() => {
            this.controls.classList.remove('is-visible');
          }, 3000);
        }
      });
    }
  }

  /**
   * Format seconds to MM:SS
   */
  formatTime(seconds) {
    if (isNaN(seconds)) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return m + ':' + (s < 10 ? '0' : '') + s;
  }

  /**
   * Update progress bar + time display
   */
  updateProgress() {
    if (!this.video.duration) return;
    const pct = (this.video.currentTime / this.video.duration) * 100;
    if (this.progressBar) this.progressBar.style.width = pct + '%';
    if (this.timeDisplay) this.timeDisplay.textContent = this.formatTime(this.video.currentTime) + ' / ' + this.formatTime(this.video.duration);
  }

  /**
   * Toggle play / pause
   */
  togglePlay() {
    if (this.video.paused || this.video.ended) {
      this.video.play();
    } else {
      this.video.pause();
    }
  }

  /**
   * Toggle mute / unmute
   */
  toggleMute() {
    this.video.muted = !this.video.muted;
    this.updateMuteState();
  }

  /**
   * Sync play/pause button state
   */
  updatePlayState() {
    if (!this.playPauseBtn) return;
    if (this.video.paused || this.video.ended) {
      this.playPauseBtn.classList.remove('is-playing');
    } else {
      this.playPauseBtn.classList.add('is-playing');
    }
  }

  /**
   * Sync mute button state
   */
  updateMuteState() {
    if (!this.muteBtn) return;
    if (this.video.muted) {
      this.muteBtn.classList.add('is-muted');
    } else {
      this.muteBtn.classList.remove('is-muted');
    }
  }

  /**
   * Seek on progress bar click
   */
  seekTo(e) {
    if (!this.video.duration) return;
    const rect = this.progressWrap.getBoundingClientRect();
    let pct = (e.clientX - rect.left) / rect.width;
    pct = Math.max(0, Math.min(1, pct));
    this.video.currentTime = pct * this.video.duration;
  }
}

customElements.define('single-video-section', singleVideoSection);